import { useState } from 'react';
import { Bell, AlertTriangle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { toast } from 'sonner';
import { currentFactoryIdAtom } from '@/stores/atoms';
import { useQueryClient } from '@tanstack/react-query';
import { useAlerts, useAlertSummary, useResolveAlert } from '@/hooks/useAlerts';
import { useTimezoneFormatting } from '@/hooks/useTimezoneFormatting';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import type { Alert } from '@/services/alert.service';

function SeverityIcon({ severity }: { severity: string }): React.JSX.Element {
  if (severity === 'error') {
    return <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />;
  }
  if (severity === 'warning') {
    return <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />;
  }
  return <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
}

function AlertRow({
  alert,
  onResolve,
  isResolving,
}: {
  alert: Alert;
  onResolve: (id: number) => void;
  isResolving: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { formatFactoryTime } = useTimezoneFormatting();

  return (
    <div className="flex items-start gap-3 p-3 hover:bg-muted/50 rounded-md transition-colors">
      <SeverityIcon severity={alert.severity} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight truncate">{alert.message}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatFactoryTime(alert.triggeredAt)}
        </p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2 text-xs flex-shrink-0"
        disabled={isResolving}
        onClick={() => onResolve(alert.id)}
      >
        {isResolving ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <CheckCircle2 className="h-3 w-3" />
        )}
        <span className="ml-1">{t('alerts.resolve')}</span>
      </Button>
    </div>
  );
}

export function NotificationBell(): React.JSX.Element {
  const { t } = useTranslation();
  const factoryId = useAtomValue(currentFactoryIdAtom);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [resolvingAll, setResolvingAll] = useState(false);

  const { data, isLoading } = useAlerts(factoryId ?? '', { resolved: false, limit: 20 });
  const { data: summary } = useAlertSummary(factoryId ?? '');
  const resolveAlertMutation = useResolveAlert(factoryId ?? '');

  const activeAlerts = data?.alerts ?? [];
  const count = summary?.active ?? activeAlerts.length;

  const invalidateAlerts = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.lists(factoryId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.summary(factoryId ?? '') });
  };

  const handleResolve = async (alertId: number): Promise<void> => {
    setResolvingId(alertId);
    try {
      await resolveAlertMutation.mutateAsync(alertId);
      toast.success(t('alerts.resolved_success'));
      invalidateAlerts();
    } catch {
      toast.error(t('alerts.resolved_error'));
    } finally {
      setResolvingId(null);
    }
  };

  const handleResolveAll = async (): Promise<void> => {
    setResolvingAll(true);
    try {
      await Promise.all(activeAlerts.map(alert => resolveAlertMutation.mutateAsync(alert.id)));
      toast.success(t('alerts.resolve_all_success'));
      invalidateAlerts();
    } catch {
      toast.error(t('alerts.resolve_all_error'));
    } finally {
      setResolvingAll(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 p-0"
          aria-label={t('alerts.bell_tooltip')}
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 min-w-5 rounded-full px-1 text-[10px] font-bold"
            >
              {count > 99 ? '99+' : count}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{t('alerts.active')}</h3>
            {count > 0 && (
              <Badge variant="secondary" className="text-xs">
                {count}
              </Badge>
            )}
          </div>
          {count > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              disabled={resolvingAll}
              onClick={handleResolveAll}
            >
              {resolvingAll ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3 w-3" />
              )}
              <span className="ml-1">{t('alerts.resolve_all')}</span>
            </Button>
          )}
        </div>
        <Separator />

        <div className="max-h-80 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && activeAlerts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-success mb-2" />
              <p className="text-sm font-medium">{t('alerts.no_active_alerts')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('alerts.no_active_alerts_desc')}
              </p>
            </div>
          )}

          {!isLoading && activeAlerts.length > 0 && (
            <div className="p-1">
              {activeAlerts.map(alert => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  onResolve={handleResolve}
                  isResolving={resolvingId === alert.id}
                />
              ))}
            </div>
          )}
        </div>

        <Separator />
        <div className="px-4 py-2">
          <Link
            to="/alerts"
            className="text-xs text-brand-teal hover:underline"
            onClick={() => setOpen(false)}
          >
            {t('alerts.view_all')}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
